import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  ClickAwayListener,
  CircularProgress,
} from '@mui/material';
import Alert from 'src/components/alert/Alert';
import { AddressSearchResult } from './types';
import AddressService from 'src/services/address-service';
import styles from 'src/components/text-input/text-input.module.scss';

interface AddressSearchFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string | React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onAddressSelect: (address: AddressSearchResult) => void;
  onManualEntry?: () => void;
  placeholder?: string;
  errorText?: string;
  disabled?: boolean;
}

export const AddressSearchField: React.FC<AddressSearchFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onAddressSelect,
  onManualEntry,
  onBlur,
  placeholder,
  errorText,
  disabled,
}) => {
  const [searchResults, setSearchResults] = useState<AddressSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  const searchAddresses = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSearchResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await AddressService.searchAddresses(query);
      setSearchResults(results);
      setIsOpen(true);
    } catch (error) {
      console.error('Error searching addresses:', error);
      setSearchResults([]);
      setIsOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange(event); // Pass the event to formik's onChange

    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new timer for debounced search
    const timer = setTimeout(() => {
      searchAddresses(newValue);
    }, 300);

    setDebounceTimer(timer);
  };

  const handleAddressSelect = (address: AddressSearchResult) => {
    // Create a synthetic ChangeEvent for formik
    const syntheticEvent = {
      target: {
        name,
        value: address.formatted,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
    onAddressSelect(address);
    setIsOpen(false);
    setSearchResults([]);
  };

  const handleManualEntryClick = () => {
    setIsOpen(false);
    setSearchResults([]);
    if (onManualEntry) {
      onManualEntry();
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        className={styles.container}
        sx={{ position: 'relative', width: '100%' }}>
        <span className={styles.label}>{label}</span>
        <Box
          className={`${styles.inputContainer} ${errorText && styles.error}`}>
          <input
            type="text"
            name={name}
            placeholder={placeholder}
            className={styles.input}
            disabled={disabled}
            value={value}
            onChange={handleInputChange}
            onBlur={onBlur}
            onFocus={() => {
              if (searchResults.length > 0) {
                setIsOpen(true);
              }
            }}
          />
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                right: 15,
                top: '60%',
                transform: 'translateY(-50%)',
              }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>

        {isOpen && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              maxHeight: '200px',
              overflow: 'auto',
              boxShadow: 3,
              marginTop: '4px',
            }}>
            <List>
              {searchResults.map((address, index) => (
                <ListItem
                  key={index}
                  onClick={() => handleAddressSelect(address)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}>
                  <ListItemText primary={address.formatted} />
                </ListItem>
              ))}
              <ListItem
                onClick={handleManualEntryClick}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  fontStyle: 'italic',
                  color: 'primary.main',
                }}>
                <ListItemText primary="Enter address manually" />
              </ListItem>
            </List>
          </Paper>
        )}

        <Alert message={errorText} />
      </Box>
    </ClickAwayListener>
  );
};
